import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AcknowledgeModal, ActionModal, Flex, FormField, Modal, TextInput, Typography } from '@percent/lemonade'
import { useAuthState } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useMutation } from '@percent/utility'

type RenameKeySetModalProps = {
  keySetId: string
  keySetName: string
  open: boolean
  onClose: () => void
  refetchApiKeys: () => void
}

export function RenameKeySetModal({ keySetId, keySetName, open, onClose, refetchApiKeys }: RenameKeySetModalProps) {
  const { t } = useTranslation()
  const [name, setName] = useState<string>('')
  const [status, setStatus] = useState<'action' | 'success' | 'error'>('action')
  const { partnerService } = useServices()
  const { partner } = useAuthState()
  const initialName = useRef(keySetName)

  const [, { apiFunc }] = useMutation(
    partnerService.editKeySet,
    () => {
      refetchApiKeys()
      setStatus('success')
    },
    () => {
      setStatus('error')
    }
  )

  const handleSubmit = () => {
    if (partner?.id) {
      apiFunc({ partnerId: partner.id, keySetId, payload: { name } })
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      {status === 'action' && (
        <ActionModal
          title={t('typography.renameKeySet')}
          primaryButtonText={t('typography.renameKeySet')}
          primaryBtnTestId="rename-key-set-btn"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="cancel-rename-key-set-btn"
          handleClose={onClose}
          handleSubmit={handleSubmit}
          disabled={!name}
        >
          <Flex direction="column" gap={16}>
            <Typography variant="default" $color="gray400">
              {t('keyList.renameKeySet.description', { keySetName })}
            </Typography>
            <FormField label={t('keyList.renameKeySet.label')} necessity="required">
              <TextInput
                name="name"
                placeholder={t('keyList.renameKeySet.placeholder')}
                onChange={e => setName(e.target.value)}
                value={name}
              />
            </FormField>
          </Flex>
        </ActionModal>
      )}
      {status === 'success' && (
        <AcknowledgeModal
          title={t('keyList.renameKeySet.success.title')}
          result="positive"
          handleClose={onClose}
          buttonText={t('button.close')}
          description={t('keyList.renameKeySet.success.description', {
            keySetName: initialName.current,
            name
          })}
        />
      )}
      {status === 'error' && (
        <AcknowledgeModal
          result="negative"
          title={t('keyList.renameKeySet.error.title')}
          handleClose={onClose}
          description={t('keyList.renameKeySet.error.description', { keySetName })}
          buttonText={t('button.close')}
        />
      )}
    </Modal>
  )
}
